import * as React from "react";
import { z } from "zod";
import { DEFAULT_NAVIGATE_OPTIONS } from "~/shared/constants/default-navigate-options.constants";
import { useSharedTransition } from "~/shared/context/SharedTransition";
import { useTransitionSearchParams } from "~/shared/hooks/useTransitionSearchParams";

export const PARTNERS_LIST_FILTERS = {
  CSM: "csm",
  ALL: "all",
} as const;

export type PartnersListFilterType = (typeof PARTNERS_LIST_FILTERS)[keyof typeof PARTNERS_LIST_FILTERS];

export const PARTNERS_LIST_SEARCH_PARAMS = {
  FILTER: "filter",
  PAGE: "page",
  TERM: "term",
} as const;

export const PartnersListSearchParamSchema = z.object({
  filter: z.enum([PARTNERS_LIST_FILTERS.CSM, PARTNERS_LIST_FILTERS.ALL]).optional().catch(PARTNERS_LIST_FILTERS.CSM),
  page: z.coerce.number().min(1).catch(1),
  term: z.string().max(50).catch(""),
});

export function getPartnersListSearchParams(searchParams: URLSearchParams) {
  const filter = searchParams.get(PARTNERS_LIST_SEARCH_PARAMS.FILTER);
  const page = searchParams.get(PARTNERS_LIST_SEARCH_PARAMS.PAGE);
  const term = searchParams.get(PARTNERS_LIST_SEARCH_PARAMS.TERM);

  return PartnersListSearchParamSchema.parse({
    [PARTNERS_LIST_SEARCH_PARAMS.FILTER]: filter,
    [PARTNERS_LIST_SEARCH_PARAMS.PAGE]: page,
    [PARTNERS_LIST_SEARCH_PARAMS.TERM]: term,
  });
}

export function usePartnersListSearchParamActions(options = DEFAULT_NAVIGATE_OPTIONS) {
  const [, setSearchParams] = useTransitionSearchParams();
  const [isPending] = useSharedTransition();

  const setPage = React.useCallback(
    (page: number) => {
      setSearchParams((prev) => {
        prev.set(PARTNERS_LIST_SEARCH_PARAMS.PAGE, page.toString());

        return prev;
      }, options);
    },

    [setSearchParams, options],
  );

  const setFilter = React.useCallback(
    (filter: string) => {
      setSearchParams((prev) => {
        prev.set(PARTNERS_LIST_SEARCH_PARAMS.FILTER, filter);
        prev.set(PARTNERS_LIST_SEARCH_PARAMS.PAGE, "1");
        return prev;
      }, options);
    },
    [setSearchParams, options],
  );

  const setSearch = React.useCallback(
    (search: string) => {
      setSearchParams((prev) => {
        if (search) {
          prev.set(PARTNERS_LIST_SEARCH_PARAMS.TERM, search);
        } else {
          prev.delete(PARTNERS_LIST_SEARCH_PARAMS.TERM);
        }

        prev.set(PARTNERS_LIST_SEARCH_PARAMS.PAGE, "1");

        return prev;
      }, options);
    },

    [setSearchParams, options],
  );

  const actions = React.useMemo(
    () => ({
      filter: {
        set: setFilter,
      },
      page: {
        set: setPage,
      },
      term: {
        set: setSearch,
      },
    }),
    [setFilter, setPage, setSearch],
  );

  return {
    isPending,
    actions,
  };
}
